import { db } from "./FirebaseConfig";
import { doc, setDoc, getDoc, updateDoc, arrayUnion } from "firebase/firestore";
import {
  specialSpotifyPlaylist,
  specialSpotifyPlaylistType,
  Week,
} from "./models"; // 🔹 Import class, not interface
import { collection, getDocs, deleteDoc } from "firebase/firestore";

/**
 * Pushes the entire data structure into a single Firestore document.
 * @param {Quarter[]} quarters - An array of Quarter class instances
 */
export const pushDataToFirestore = async (weeks: Week[]): Promise<void> => {
  try {
    const docRef = doc(
      db,
      "picksData",
      "picksDataDocument",
      "QuartersCollection",
      "SQ24"
    );
    await setDoc(docRef, {
      weeks: weeks.map((w) => w.toFirestore(), { merge: true }),
    });

    console.log("✅ Data successfully added to Firestore!");
  } catch (error) {
    console.error("❌ Error adding data to Firestore:", error);
  }
};

/**
 * Deletes all documents from the `picksData` collection in Firestore.
 */
export const resetFirestoreDatabase = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, "picksData"));

    querySnapshot.forEach(async (document) => {
      await deleteDoc(doc(db, "picksData", document.id));
    });

    console.log("✅ Firestore database has been reset.");
  } catch (error) {
    console.error("❌ Error resetting Firestore database:", error);
  }
};

export async function getBusinessConfig() {
  const docRef = doc(db, "spotifyPlaylistDB", "playlists"); // creates reference to document
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const business_config = docSnap.data();
    console.log(business_config);
    return business_config;
  }
  return null;
}

export const addToExistingDocument = async (
  newWeeks: Week[]
): Promise<void> => {
  try {
    const docRef = doc(
      db,
      "picksData",
      "picksDataDocument",
      "QuartersCollection",
      "SQ24"
    );

    // Get the existing document
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      // If the document exists, merge new data into the existing quarters array
      const existingData = docSnap.data();
      const existingWeeks = existingData.quarters || [];

      // Merge new data with existing data
      const updatedWeeks = [
        ...existingWeeks,
        ...newWeeks.map((q) => q.toFirestore()),
      ];

      // Update Firestore with merged data
      await setDoc(docRef, { weeks: updatedWeeks }, { merge: true });

      console.log("✅ Data successfully merged into the existing document!");
    } else {
      // If the document doesn't exist, create it with new data
      await setDoc(docRef, {
        weeks: newWeeks.map((w) => w.toFirestore()),
      });

      console.log("✅ New document created with initial data!");
    }
  } catch (error) {
    console.error("❌ Error adding data to Firestore:", error);
  }
};

export const addSpotifyPlaylist = async (
  newSpecialPlaylists: specialSpotifyPlaylist[]
): Promise<void> => {
  try {
    const docRef = doc(db, "spotifyPlaylistDB", "playlists");

    // Get the existing document
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      // If the document exists, merge new data into the existing quarters array
      const existingData = docSnap.data();
      const existingPlaylists = existingData.playlistArr || [];

      // Merge new data with existing data
      const updatedPlaylists = [
        ...existingPlaylists,
        ...newSpecialPlaylists.map((nsp) => nsp.toFirestore()),
      ];

      // Update Firestore with merged data
      await setDoc(docRef, { playlistArr: updatedPlaylists }, { merge: true });

      console.log("✅ Data successfully merged into the existing document!");
    } else {
      // If the document doesn't exist, create it with new data
      await setDoc(docRef, {
        weeks: newSpecialPlaylists.map((nsp) => nsp.toFirestore()),
      });

      console.log("✅ New document created with initial data!");
    }
  } catch (error) {
    console.error("❌ Error adding data to Firestore:", error);
  }
};

export const fetchSpecialSpotifyPlaylists = async (): Promise<
  specialSpotifyPlaylistType[]
> => {
  try {
    const docRef = doc(db, "spotifyPlaylistDB", "playlists");

    // Get the existing document
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const existingData = docSnap.data();
      return existingData.playlistArr || [];
    } else {
      console.log("DNE");
      let emptyArr: specialSpotifyPlaylistType[] = [];
      return emptyArr;
    }
  } catch (error) {
    console.error("❌ Error adding data to Firestore:", error);
    return [];
  }
};

export const fetchWheelData = async (docId: string): Promise<string[]> => {
  try {
    const docRef = doc(db, "nameWheel", docId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const existingData = docSnap.data();
      return existingData.names || [];
    } else {
      console.log("DNE");
      let emptyNames: string[] = [];
      return emptyNames;
    }
  } catch (error) {
    console.error("❌ Error fetching data", error);
    return [];
  }
};
export const fetchSocialLink = async <T>(docId: string): Promise<T[]> => {
  try {
    const docRef = doc(db, "socialsLinks", docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data().link || [];
    } else {
      console.log("DNE");
      return [];
    }
  } catch (error) {
    console.error("❌ Error fetching social link:", error);
    return [];
  }
};

export const replaceSocialLink = async (
  docId: string,
  newLinks: { toFirestore: () => any }[]
): Promise<void> => {
  try {
    const docRef = doc(db, "socialsLinks", docId);
    const docSnap = await getDoc(docRef);

    const updatedLinks = newLinks.map((link) => link.toFirestore());

    await setDoc(docRef, { link: updatedLinks });

    console.log("✅ Link field successfully overwritten in the document!");
  } catch (error) {
    console.error("❌ Error updating social link:", error);
  }
};

export const deleteWheelEntry = async (
  category: "Albums" | "Songs" | "OnceThrough",
  item: string
) => {
  try {
    const ref = doc(db, "nameWheel", category);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      console.error(`Document for ${category} does not exist`);
      return;
    }
    const data = snap.data();
    if (!data.names || !Array.isArray(data.names)) {
      console.error(`No 'items' array found in ${category}`);
      return;
    }
    const filtered = data.names.filter((entry: string) => entry !== item);
    await updateDoc(ref, { names: filtered });
    console.log(`Deleted "${item}" from ${category}`);
  } catch (error) {
    console.error("Error deleting wheel entry:", error);
  }
};

export const addWheelEntry = async (docId: string, name: string) => {
  const ref = doc(db, "nameWheel", docId);
  await updateDoc(ref, {
    names: arrayUnion(name),
  });
};

export const updateNextWeeksPick = async (field: string, value: string) => {
  const ref = doc(db, "picksToAdd", "nextWeeksPicks");
  await updateDoc(ref, {
    [`nextWeek.${field}`]: value,
  });
};
